/**
 * RichTextField — text input for a TextOverlay that renders styled runs as
 * atomic inline chips (Lexical token-mode text nodes).
 *
 * Semantics (see design in project memory):
 * - Plain text is edited freely; typing always produces base-styled text.
 * - A styled run renders as a wrapping inline chip. It cannot be edited or
 *   caret-entered here; clicking it selects the run (onRunSelect) so the
 *   panel can switch to run-scoped editing. Backspace deletes a chip whole
 *   (text and style) — that is Lexical token behavior.
 * - Deliberately location-agnostic: no knowledge of the panel it sits in, so
 *   it can later be remounted over the canvas for in-place editing.
 */
import React, { useEffect, useRef, useState } from "react";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { PlainTextPlugin } from "@lexical/react/LexicalPlainTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { Button } from "./ui/button";
import {
  $createLineBreakNode,
  $createParagraphNode,
  $createTextNode,
  $getRoot,
  $getSelection,
  $getNearestNodeFromDOMNode,
  $isElementNode,
  $isLineBreakNode,
  $isRangeSelection,
  $isTextNode,
  CLICK_COMMAND,
  COMMAND_PRIORITY_LOW,
  EditorConfig,
  LexicalEditor,
  NodeKey,
  SELECTION_CHANGE_COMMAND,
  SerializedTextNode,
  Spread,
  TextNode,
} from "lexical";
import {
  TextRun,
  TextRunStyle,
  normalizeTextRuns,
  textRunStylesEqual,
  textRunsText,
} from "../models/Post";

// ---------------------------------------------------------------------------
// RunChipNode: a token-mode TextNode carrying a TextRunStyle
// ---------------------------------------------------------------------------

type SerializedRunChipNode = Spread<{ runStyle: TextRunStyle }, SerializedTextNode>;

/**
 * All chips share one neutral pill look — the canvas shows the run's actual
 * styling; the field only marks WHICH text is styled and that it's clickable.
 */
function applyChipStyles(dom: HTMLElement): void {
  dom.style.cursor = "pointer";
  dom.style.borderRadius = "4px";
  dom.style.padding = "1px 4px";
  dom.style.backgroundColor = "rgba(120, 120, 140, 0.15)";
  dom.style.boxShadow = "inset 0 0 0 1px rgba(120, 120, 140, 0.35)";
  // Wrap long chips mid-phrase as pill fragments instead of one rigid block
  const st = dom.style as CSSStyleDeclaration & Record<string, string>;
  st.boxDecorationBreak = "clone";
  st.webkitBoxDecorationBreak = "clone";
}

export class RunChipNode extends TextNode {
  __runStyle: TextRunStyle;

  static getType(): string {
    return "wavegen-run-chip";
  }

  static clone(node: RunChipNode): RunChipNode {
    return new RunChipNode(node.__text, node.__runStyle, node.__key);
  }

  constructor(text: string, runStyle: TextRunStyle, key?: NodeKey) {
    super(text, key);
    this.__runStyle = runStyle;
  }

  createDOM(config: EditorConfig): HTMLElement {
    const dom = super.createDOM(config);
    applyChipStyles(dom);
    return dom;
  }

  updateDOM(prevNode: this, dom: HTMLElement, config: EditorConfig): boolean {
    const updated = super.updateDOM(prevNode, dom, config);
    applyChipStyles(dom);
    return updated;
  }

  static importJSON(json: SerializedRunChipNode): RunChipNode {
    const node = $createRunChipNode(json.text, json.runStyle);
    node.setFormat(json.format);
    node.setDetail(json.detail);
    node.setStyle(json.style);
    return node;
  }

  exportJSON(): SerializedRunChipNode {
    return {
      ...super.exportJSON(),
      type: RunChipNode.getType(),
      runStyle: this.getRunStyle(),
    };
  }

  getRunStyle(): TextRunStyle {
    return this.getLatest().__runStyle;
  }
}

export function $createRunChipNode(text: string, runStyle: TextRunStyle): RunChipNode {
  const node = new RunChipNode(text, runStyle);
  // Token mode = atomic: caret skips over it, it cannot be typed into, and
  // delete/backspace removes it whole
  node.setMode("token");
  return node;
}

// ---------------------------------------------------------------------------
// Serialization: editor state <-> {text, runs}
// ---------------------------------------------------------------------------

interface FieldValue {
  text: string;
  runs?: TextRun[];
}

/** Must run inside editor.read()/update(). Also reports each styled run's node key. */
function $serializeToRuns(): { value: FieldValue; chipKeys: Map<number, NodeKey> } {
  const root = $getRoot();
  const runs: TextRun[] = [];
  const chipKeys = new Map<number, NodeKey>();
  let buf = "";
  const flush = () => {
    if (buf) {
      runs.push({ text: buf });
      buf = "";
    }
  };

  root.getChildren().forEach((block, blockIndex) => {
    if (blockIndex > 0) buf += "\n";
    if (!$isElementNode(block)) return;
    block.getChildren().forEach((child) => {
      if (child instanceof RunChipNode) {
        flush();
        chipKeys.set(runs.length, child.getKey());
        runs.push({ text: child.getTextContent(), style: child.getRunStyle() });
      } else if ($isLineBreakNode(child)) {
        buf += "\n";
      } else if ($isTextNode(child)) {
        buf += child.getTextContent();
      }
    });
  });
  flush();

  const text = textRunsText(runs);
  const hasStyled = runs.some((r) => r.style && Object.keys(r.style).length > 0);
  return { value: { text, runs: hasStyled ? runs : undefined }, chipKeys };
}

function valuesEqual(a: FieldValue, b: FieldValue): boolean {
  if (a.text !== b.text) return false;
  const ar = a.runs ?? [];
  const br = b.runs ?? [];
  if (ar.length !== br.length) return false;
  return ar.every(
    (r, i) => r.text === br[i].text && textRunStylesEqual(r.style, br[i].style)
  );
}

const SYNC_TAG = "wavegen-runfield-sync";

/** Must run inside editor.update(). Replaces the content with the given value
 *  and refreshes the chip-key index. */
function $rebuildFromValue(
  text: string,
  runs: TextRun[] | undefined,
  refs: SharedRefs
) {
  const root = $getRoot();
  root.clear();
  const paragraph = $createParagraphNode();
  const source: TextRun[] = runs && runs.length > 0 ? runs : text ? [{ text }] : [];
  for (const run of source) {
    if (run.style && Object.keys(run.style).length > 0) {
      paragraph.append($createRunChipNode(run.text, run.style));
    } else {
      run.text.split("\n").forEach((part, i) => {
        if (i > 0) paragraph.append($createLineBreakNode());
        if (part) paragraph.append($createTextNode(part));
      });
    }
  }
  root.append(paragraph);
  const { chipKeys } = $serializeToRuns();
  refs.chipKeys.current = chipKeys;
}

// ---------------------------------------------------------------------------
// Plugins
// ---------------------------------------------------------------------------

interface SharedRefs {
  lastValue: React.MutableRefObject<FieldValue | null>;
  chipKeys: React.MutableRefObject<Map<number, NodeKey>>;
}

/** Rebuild editor content when the external value changes (panel edits, undo at post level, overlay switch). */
function ExternalSyncPlugin({
  text,
  runs,
  refs,
}: {
  text: string;
  runs?: TextRun[];
  refs: SharedRefs;
}) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    const incoming: FieldValue = { text, runs };
    if (refs.lastValue.current && valuesEqual(refs.lastValue.current, incoming)) {
      return;
    }
    refs.lastValue.current = incoming;
    // Stale-runs defense: if the invariant concat(runs.text) === text is
    // broken, `text` is the truth (matches getRenderableRuns' fallback) —
    // otherwise the field would display the stale runs' words and the first
    // keystroke would write them back over the overlay's text.
    const safeRuns = runs && textRunsText(runs) === text ? runs : undefined;
    editor.update(() => $rebuildFromValue(text, safeRuns, refs), {
      tag: SYNC_TAG,
    });
  }, [editor, text, runs, refs]);

  return null;
}

/** Emit user edits upward; skips our own sync updates via the tag. */
function EmitChangesPlugin({
  onChange,
  refs,
}: {
  onChange: (value: FieldValue) => void;
  refs: SharedRefs;
}) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState, tags, dirtyElements, dirtyLeaves }) => {
      if (tags.has(SYNC_TAG)) return;
      if (dirtyElements.size === 0 && dirtyLeaves.size === 0) return;
      const { value, chipKeys } = editorState.read(() => $serializeToRuns());
      refs.chipKeys.current = chipKeys;
      const normalized: FieldValue = {
        text: value.text,
        runs: value.runs ? normalizeTextRuns(value.runs) : undefined,
      };
      // When normalization changed the runs (an edit made two same-styled
      // chips adjacent and they merged), the editor's chips no longer match
      // what we emit — and the round-trip compares equal, so ExternalSync
      // won't rebuild. Rebuild here so chip indices stay aligned.
      if (value.runs && !valuesEqual(value, normalized)) {
        editor.update(() => $rebuildFromValue(normalized.text, normalized.runs, refs), {
          tag: SYNC_TAG,
        });
      }
      if (refs.lastValue.current && valuesEqual(refs.lastValue.current, normalized)) {
        return;
      }
      refs.lastValue.current = normalized;
      onChange(normalized);
    });
  }, [editor, onChange, refs]);

  return null;
}

/** Click a chip -> report its index in the serialized runs array. */
function ChipClickPlugin({
  onRunSelect,
  refs,
}: {
  onRunSelect?: (runIndex: number | null) => void;
  refs: SharedRefs;
}) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    if (!onRunSelect) return;
    return editor.registerCommand(
      CLICK_COMMAND,
      (event: MouseEvent) => {
        let clickedKey: NodeKey | null = null;
        // editor.read (not editorState.read): $getNearestNodeFromDOMNode
        // requires an active editor context
        editor.read(() => {
          const node = $getNearestNodeFromDOMNode(event.target as Node);
          if (node instanceof RunChipNode) clickedKey = node.getKey();
        });
        if (clickedKey !== null) {
          for (const [index, key] of refs.chipKeys.current) {
            if (key === clickedKey) {
              onRunSelect(index);
              return false;
            }
          }
        }
        onRunSelect(null);
        return false;
      },
      COMMAND_PRIORITY_LOW
    );
  }, [editor, onRunSelect, refs]);

  return null;
}

/**
 * Absolute offset of a selection point within the serialized text (same
 * coordinate space as {text, runs}: blocks joined with "\n", line breaks
 * count as one character). Must run inside an editor read.
 */
function $pointOffset(point: { type: string; key: NodeKey; offset: number }): number {
  let offset = 0;
  const blocks = $getRoot().getChildren();
  for (let bi = 0; bi < blocks.length; bi++) {
    if (bi > 0) offset += 1; // block boundary = "\n"
    const block = blocks[bi];
    if (!$isElementNode(block)) continue;
    if (point.type === "element" && point.key === block.getKey()) {
      for (const child of block.getChildren().slice(0, point.offset)) {
        offset += $isLineBreakNode(child) ? 1 : child.getTextContent().length;
      }
      return offset;
    }
    for (const child of block.getChildren()) {
      if (point.type === "text" && child.getKey() === point.key) {
        return offset + point.offset;
      }
      offset += $isLineBreakNode(child) ? 1 : child.getTextContent().length;
    }
  }
  return offset;
}

/**
 * The current selection's text when it may become a highlight: a non-empty
 * single-line range over plain text only — no existing chips inside.
 * Must run inside an editor read/update.
 */
function $highlightableSelectionText(): string | null {
  const selection = $getSelection();
  if (!$isRangeSelection(selection) || selection.isCollapsed()) return null;
  const text = selection.getTextContent();
  if (!text.trim() || text.includes("\n")) return null;
  const nodes = selection.getNodes();
  if (nodes.some((n) => n instanceof RunChipNode || !$isTextNode(n))) return null;
  return text;
}

/**
 * Shows a "Highlight" action while the selection is eligible; clicking it
 * converts the selected text into a chip and reports the new run's index so
 * the panel can drill into Edit Highlight.
 */
function HighlightActionPlugin({
  onStyleSelection,
}: {
  onStyleSelection?: (range: { start: number; end: number; text: string }) => void;
}) {
  const [editor] = useLexicalComposerContext();
  const [selectionText, setSelectionText] = useState<string | null>(null);

  useEffect(() => {
    const recompute = () => {
      editor.getEditorState().read(() => {
        setSelectionText($highlightableSelectionText());
      });
    };
    const unregisterUpdate = editor.registerUpdateListener(recompute);
    const unregisterSelection = editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      () => {
        recompute();
        return false;
      },
      COMMAND_PRIORITY_LOW
    );
    return () => {
      unregisterUpdate();
      unregisterSelection();
    };
  }, [editor]);

  // No mutation here: report the selection's absolute range and let the
  // panel hold it as a pending highlight — the chip only materializes when
  // the user actually applies a style
  const reportSelection = () => {
    let range: { start: number; end: number; text: string } | null = null;
    editor.read(() => {
      const text = $highlightableSelectionText();
      const selection = $getSelection();
      if (!text || !$isRangeSelection(selection)) return;
      const a = $pointOffset(selection.anchor);
      const f = $pointOffset(selection.focus);
      range = { start: Math.min(a, f), end: Math.max(a, f), text };
    });
    setSelectionText(null);
    if (range && onStyleSelection) onStyleSelection(range);
  };

  if (!selectionText) return null;
  const label =
    selectionText.length > 24 ? `${selectionText.slice(0, 24)}…` : selectionText;
  return (
    <div className="mt-2">
      {/* Panel button recipe (like Duplicate/Delete), but tinted with the
          selection accent: this is the action of the SELECTED state, pairing
          with the field's focus border above it */}
      <Button
        type="button"
        variant="outline"
        // Keep the editor's selection alive while clicking the button
        onMouseDown={(e) => e.preventDefault()}
        onClick={reportSelection}
        aria-label={`Style “${label}” as a highlight`}
        className="wg-style-selection w-full items-center justify-center gap-1.5 border-[oklch(var(--brand)/0.45)] bg-[oklch(var(--brand)/0.06)] hover:bg-[oklch(var(--brand)/0.12)] hover:text-foreground"
      >
        Style
        {/* The selection previewed as the chip it will become */}
        <span
          aria-hidden
          className="max-w-[14rem] truncate rounded px-1.5"
          style={{
            backgroundColor: "rgba(120, 120, 140, 0.15)",
            boxShadow: "inset 0 0 0 1px rgba(120, 120, 140, 0.35)",
          }}
        >
          {label}
        </span>
        <span aria-hidden className="text-muted-foreground">
          ›
        </span>
      </Button>
    </div>
  );
}

/** Outline the chip whose run is being edited in the panel. */
function SelectedChipPlugin({
  selectedRunIndex,
  refs,
}: {
  selectedRunIndex?: number | null;
  refs: SharedRefs;
}) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    if (selectedRunIndex === null || selectedRunIndex === undefined) return;
    const key = refs.chipKeys.current.get(selectedRunIndex);
    if (key === undefined) return;
    const dom = editor.getElementByKey(key);
    if (!dom) return;
    dom.classList.add("wg-run-chip-selected");
    return () => dom.classList.remove("wg-run-chip-selected");
  });

  return null;
}

// ---------------------------------------------------------------------------
// The field
// ---------------------------------------------------------------------------

export interface RichTextFieldProps {
  text: string;
  runs?: TextRun[];
  onChange: (value: { text: string; runs?: TextRun[] }) => void;
  /** Called with the run index when a chip is clicked, null when plain text is clicked */
  onRunSelect?: (runIndex: number | null) => void;
  /** Called with the selection's absolute range when the user hits "Style" */
  onStyleSelection?: (range: { start: number; end: number; text: string }) => void;
  /** Run index currently edited in the panel; its chip gets a selection ring */
  selectedRunIndex?: number | null;
  placeholder?: string;
  className?: string;
}

const fieldCss = `
.wg-runfield [contenteditable="true"] {
  min-height: 120px;
  width: 100%;
  padding: 0.5rem;
  border: 1px solid hsl(var(--border, 214.3 31.8% 91.4%));
  border-radius: 0.375rem;
  font-size: 0.875rem;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
  outline: none;
}
.wg-runfield [contenteditable="true"]:focus {
  border-color: hsl(var(--ring, 222.2 84% 4.9%));
}
.wg-runfield .wg-run-chip-selected {
  outline: 2px solid #3b82f6;
  outline-offset: 1px;
}
.wg-style-selection {
  animation: wg-style-in 120ms ease-out;
}
@keyframes wg-style-in {
  from { opacity: 0; transform: translateY(2px); }
  to { opacity: 1; transform: none; }
}
@media (prefers-reduced-motion: reduce) {
  .wg-style-selection { animation: none; }
}
.wg-runfield-placeholder {
  position: absolute;
  top: 0.5rem;
  left: 0.5rem;
  pointer-events: none;
  color: #9ca3af;
  font-size: 0.875rem;
}
`;

export function RichTextField({
  text,
  runs,
  onChange,
  onRunSelect,
  onStyleSelection,
  selectedRunIndex,
  placeholder = "Enter your text here... (Press Enter for new lines)",
  className,
}: RichTextFieldProps) {
  const lastValue = useRef<FieldValue | null>(null);
  const chipKeys = useRef<Map<number, NodeKey>>(new Map());
  const refs: SharedRefs = { lastValue, chipKeys };

  const initialConfig = {
    namespace: "wavegen-runfield",
    nodes: [RunChipNode],
    onError: (error: Error) => {
      console.error("[RichTextField]", error);
    },
    theme: {},
  };

  return (
    <div className={`wg-runfield relative ${className ?? ""}`}>
      <style>{fieldCss}</style>
      <LexicalComposer initialConfig={initialConfig}>
        <PlainTextPlugin
          contentEditable={<ContentEditable />}
          placeholder={<div className="wg-runfield-placeholder">{placeholder}</div>}
          ErrorBoundary={LexicalErrorBoundary}
        />
        <HistoryPlugin />
        <ExternalSyncPlugin text={text} runs={runs} refs={refs} />
        <EmitChangesPlugin onChange={onChange} refs={refs} />
        <ChipClickPlugin onRunSelect={onRunSelect} refs={refs} />
        <SelectedChipPlugin selectedRunIndex={selectedRunIndex} refs={refs} />
        <HighlightActionPlugin onStyleSelection={onStyleSelection} />
      </LexicalComposer>
    </div>
  );
}

export default RichTextField;
